import React, { useEffect, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import PasswordInput from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";
import api from "@/lib/api";
import { Search, UserCheck, Utensils } from "lucide-react";
import ConsentCheckbox from "@/components/ConsentCheckbox";

const WEAVE_LOGO = "https://customer-assets-lxgj4vgw.emergentagent.net/job_essex-teams/artifacts/mur72jzi_WEAVE-LOGO%202024.webp";
const ESSEX_STARTUPS_LOGO = "https://customer-assets-lxgj4vgw.emergentagent.net/job_essex-teams/artifacts/9pfl78q8_Logo.jpg";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [roster, setRoster] = useState([]);
  const [picked, setPicked] = useState(null);
  const [pickerQ, setPickerQ] = useState("");
  const [form, setForm] = useState({
    email: "",
    password: "",
    dietary: "",
    sandwich_choice: "",
    consent: false,
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [rosterLoaded, setRosterLoaded] = useState(false);
  const [sandwichChoices, setSandwichChoices] = useState([]);

  useEffect(() => {
    api.get("/student-roster/public")
      .then(({ data }) => setRoster(data || []))
      .catch(() => setRoster([]))
      .finally(() => setRosterLoaded(true));
    // Admin-configured sandwich list (Programme Settings). Best-effort.
    api.get("/programme")
      .then(({ data }) => setSandwichChoices(data?.sandwich_choices || []))
      .catch(() => setSandwichChoices([]));
  }, []);

  const filtered = useMemo(() => {
    const norm = (s) => (s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/[^a-z0-9\s]/g, "");
    const q = norm(pickerQ.trim());
    if (!q) return roster;
    const words = q.split(/\s+/).filter(Boolean);
    return roster.filter((r) => {
      const hay = norm(`${r.full_name || ""} ${r.first_name || ""} ${r.last_name || ""}`);
      return words.every((w) => hay.includes(w));
    });
  }, [pickerQ, roster]);

  const suggestEmail = (entry) => {
    if (!entry?.full_name) return "";
    const parts = entry.full_name.toLowerCase().split(/\s+/).filter(Boolean);
    if (parts.length < 2) return `${parts[0] || ""}@essex.ac.uk`;
    const first = parts[0];
    const last = parts[parts.length - 1];
    return `${first}.${last}@essex.ac.uk`.replace(/[^a-z0-9.@-]/g, "");
  };

  const choose = (entry) => {
    setPicked(entry);
    setForm((f) => ({ ...f, email: suggestEmail(entry) }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!picked) { setError("Pick your name from the list first."); return; }
    if (!/essex\.ac\.uk$/i.test(form.email.trim())) {
      setError("Please use your University of Essex email address (ending @essex.ac.uk).");
      return;
    }
    if (sandwichChoices.length > 0 && !form.sandwich_choice) {
      setError("Please pick a sandwich for the sandwich sessions.");
      return;
    }
    if (!form.consent) {
      setError("Please confirm you agree to be contacted about the programme.");
      return;
    }
    setLoading(true);
    try {
      await register({
        name: picked.full_name,
        email: form.email,
        password: form.password,
        role: "student",
        dietary: form.dietary.trim() || null,
        sandwich_choice: form.sandwich_choice || null,
        roster_id: picked.id || null,
        consent: true,
      });
      navigate("/team-setup", { replace: true });
    } catch (e) {
      setError(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-[#FDFDFD]" data-testid="register-page">
      <div className="w-full max-w-lg">
        <div className="flex items-center justify-between gap-4 mb-8">
          <img src={ESSEX_STARTUPS_LOGO} alt="Essex Startups" className="h-14 w-14 object-cover" />
          <div className="text-center leading-tight">
            <div className="font-display text-lg font-black tracking-tight text-[#0F1F2E]">iTeams</div>
            <div className="text-[10px] font-semibold tracking-[0.16em] uppercase text-[#525252]">University of Essex</div>
          </div>
          <img src={WEAVE_LOGO} alt="The Weave" className="h-12 w-auto object-contain" />
        </div>

        <div className="text-[10px] font-semibold tracking-[0.24em] uppercase text-[#25AAAA]">Register</div>
        <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tighter mt-2 text-[#0F1F2E]">Join iTeams.</h1>
        <p className="mt-3 text-sm text-[#525252]">
          Pick your name from the cohort list below, confirm your email, tell us about food, set a password — and you&apos;re in.
        </p>

        <form onSubmit={onSubmit} className="mt-8 space-y-5" data-testid="register-form">
          <div>
            <Label className="text-[10px] font-semibold tracking-[0.16em] uppercase text-[#525252]">Step 1 · Find your name</Label>
            {picked ? (
              <div className="mt-2 border-2 border-[#25AAAA] bg-[#E6F7F7] px-4 py-3 flex items-center justify-between" data-testid="register-picked">
                <div className="flex items-center gap-3">
                  <UserCheck size={16} className="text-[#1e7d7d]" />
                  <div>
                    <div className="font-display text-base font-bold tracking-tight text-[#0F1F2E]">{picked.full_name}</div>
                    <div className="text-[11px] uppercase tracking-wider text-[#525252] font-mono">{picked.raw}</div>
                  </div>
                </div>
                <button type="button" className="text-[11px] uppercase tracking-wider text-[#25AAAA] hover:text-[#1e7d7d] font-semibold"
                  onClick={() => { setPicked(null); setForm((f) => ({ ...f, email: "" })); }}
                  data-testid="register-change-pick">Change</button>
              </div>
            ) : (
              <>
                <div className="relative mt-2">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#525252]" />
                  <Input placeholder="Type your name…" value={pickerQ} onChange={(e) => setPickerQ(e.target.value)}
                    className="rounded-none h-11 pl-9" data-testid="register-picker-search"/>
                </div>
                {!rosterLoaded ? (
                  <div className="text-xs text-[#525252] mt-2">Loading roster…</div>
                ) : (
                  <div className="mt-2 border border-[#E5E7EB] bg-white max-h-56 overflow-y-auto divide-y divide-[#E5E7EB]" data-testid="register-picker-list">
                    {filtered.length === 0 ? (
                      <div className="px-4 py-4 text-xs text-[#525252]">
                        {roster.length === 0
                          ? "The roster is empty. Ask your admin to add your name."
                          : "No matches. Ask your admin to add you if you're missing."}
                      </div>
                    ) : filtered.map((r) => r.registered ? (
                      <div key={r.id} className="px-4 py-2.5 text-sm flex items-center justify-between gap-3 bg-[#FEF9E7]" data-testid={`register-already-${r.id}`}>
                        <div className="min-w-0">
                          <span className="font-medium text-[#0F1F2E]">{r.full_name}</span>
                          <div className="text-[11px] text-[#7A5B17]">Already registered{r.email_hint ? ` as ${r.email_hint}` : ""} — sign in instead, or reset your password.</div>
                        </div>
                        <Link to="/login" className="text-[10px] uppercase tracking-wider text-[#0F1F2E] underline shrink-0" data-testid={`register-already-login-${r.id}`}>Sign in</Link>
                      </div>
                    ) : (
                      <button key={r.id} type="button" onClick={() => choose(r)}
                        className="w-full text-left px-4 py-2.5 hover:bg-[#F3F4F6] text-sm flex items-center justify-between"
                        data-testid={`register-pick-${r.id}`}>
                        <span className="font-medium text-[#0F1F2E]">{r.full_name}</span>
                        <span className="text-[10px] uppercase tracking-wider text-[#525252]">Pick</span>
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>

          <div>
            <Label className="text-[10px] font-semibold tracking-[0.16em] uppercase text-[#525252]">Step 2 · Your Essex email</Label>
            <Input type="email" required value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              className="mt-2 rounded-none h-11" placeholder="your.email@essex.ac.uk"
              data-testid="register-email-input"/>
            <p className="text-[11px] text-[#525252] mt-1">
              Please use your <strong>@essex.ac.uk</strong> address so the platform matches you to your team and attendance.
            </p>
          </div>

          <div>
            <Label className="text-[10px] font-semibold tracking-[0.16em] uppercase text-[#525252]">Step 3 · Dietary requirements</Label>
            <Textarea
              value={form.dietary}
              onChange={(e) => setForm((f) => ({ ...f, dietary: e.target.value }))}
              className="mt-2 rounded-none min-h-[70px]"
              placeholder="e.g. Vegetarian, gluten-free, nut allergy — or 'None'"
              data-testid="register-dietary-input"
            />
            <p className="text-[11px] text-[#525252] mt-1">
              Anything we or the caterer needs to know. This stays with your record.
            </p>
          </div>

          {sandwichChoices.length > 0 && (
            <div>
              <Label className="text-[10px] font-semibold tracking-[0.16em] uppercase text-[#525252] flex items-center gap-2">
                <Utensils size={12} /> Step 4 · Sandwich choice
              </Label>
              <p className="text-[11px] text-[#525252] mt-1 mb-2">
                For the sessions with sandwiches on offer — pick one from Mission Café&apos;s list.
              </p>
              <div className="space-y-2" data-testid="register-sandwich-list">
                {sandwichChoices.map((opt) => (
                  <label
                    key={opt}
                    className={`flex items-start gap-2 text-sm p-3 border cursor-pointer transition-colors ${
                      form.sandwich_choice === opt
                        ? "border-[#25AAAA] bg-[#E6F7F7]"
                        : "border-[#E5E7EB] bg-white hover:border-[#25AAAA]"
                    }`}
                    data-testid={`register-sandwich-${opt.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                  >
                    <input
                      type="radio"
                      name="sandwich_choice"
                      checked={form.sandwich_choice === opt}
                      onChange={() => setForm((f) => ({ ...f, sandwich_choice: opt }))}
                      className="mt-1"
                    />
                    <span>{opt}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          <div>
            <Label className="text-[10px] font-semibold tracking-[0.16em] uppercase text-[#525252]">
              Step {sandwichChoices.length > 0 ? "5" : "4"} · Set a password
            </Label>
            <PasswordInput required minLength={6} value={form.password}
              onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
              className="mt-2 rounded-none h-11" data-testid="register-password-input"/>
            <p className="text-[11px] text-[#525252] mt-1">At least 6 characters. Only used on this platform.</p>
          </div>

          <ConsentCheckbox checked={form.consent} onChange={(v) => setForm((f) => ({ ...f, consent: v }))} />

          {error && (
            <div className="border border-[#E93E36] bg-[#FDEDED] px-3 py-2 text-sm text-[#7A1F1F]" data-testid="register-error">{error}</div>
          )}

          <Button type="submit" disabled={loading || !picked}
            className="w-full h-11 rounded-none bg-[#25AAAA] hover:bg-[#1e7d7d] uppercase tracking-wide text-xs font-semibold"
            data-testid="register-submit-btn">
            {loading ? "Creating…" : "Create my account"}
          </Button>
        </form>

        <div className="mt-6 text-sm text-[#525252]">
          Already have an account?{" "}
          <Link to="/login" className="text-[#25AAAA] font-medium hover:underline" data-testid="link-login">Sign in</Link>
        </div>
      </div>
    </div>
  );
}
